import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { UserIcon, ClipboardDocumentCheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const RegisterLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const redirectQuery = redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : '';
  return (
    <div className="min-h-screen flex">
      {/* Left Side - Brand Visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-clinic-cream via-clinic-cream-light to-clinic-primary/5 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-clinic-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-clinic-gold/10 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-clinic-primary/10 rounded-full" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 lg:px-20">
          {/* Logo */}
          <img
            src="/logos/logo-primary.png"
            alt="Clinic Studios"
            className="h-16 w-auto mb-12 object-contain object-left"
          />

          {/* Tagline */}
          <h1 className="text-4xl lg:text-5xl font-bold text-clinic-dark font-body leading-tight mb-6">
            Join our
            <br />
            <span className="text-clinic-primary">wellness community</span>
          </h1>

          <p className="text-lg text-clinic-dark/70 max-w-md mb-8 leading-relaxed">
            Whether you're seeking care or providing it, Clinic Studios offers a supportive space for everyone.
          </p>

          {/* Trust indicators */}
          <div className="space-y-4">
            {[
              'Secure & PIPEDA compliant',
              'Trusted by mental health and allied professionals',
              'Easy to get started',
            ].map((feature, index) => (
              <div key={index} className="flex items-center text-clinic-dark/70">
                <div className="w-2 h-2 bg-clinic-primary rounded-full mr-3" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom decoration */}
        <div className="absolute bottom-8 left-12 lg:left-20">
          <img
            src="/logos/logo-icon.png"
            alt=""
            className="h-10 w-auto opacity-30"
          />
        </div>
      </div>

      {/* Right Side - Registration Options */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <img
              src="/logos/logo-primary.png"
              alt="Clinic Studios"
              className="h-12 w-auto mx-auto mb-4"
            />
          </div>

          {/* Header */}
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-clinic-dark font-body">
              Create an Account
            </h2>
            <p className="mt-2 text-clinic-muted">
              Choose how you'd like to join Clinic Studios
            </p>
          </div>

          {/* Registration Options */}
          <div className="space-y-4">
            {/* Patient Option */}
            <Link
              to={`/register/patient${redirectQuery}`}
              className="group block w-full p-6 bg-white border-2 border-clinic-primary-lighter rounded-card shadow-clinic-sm hover:shadow-clinic hover:border-clinic-primary transition-all"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-clinic-primary/10 rounded-full flex items-center justify-center group-hover:bg-clinic-primary/20 transition-colors">
                    <UserIcon className="w-7 h-7 text-clinic-primary" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-clinic-dark font-body">I'm a Client</h3>
                  <p className="text-sm text-clinic-muted mt-1">
                    Book appointments with mental health or allied professionals
                  </p>
                </div>
                <div className="ml-4">
                  <ArrowRightIcon className="w-5 h-5 text-clinic-muted group-hover:text-clinic-primary group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>

            {/* Clinician Option */}
            <Link
              to={`/register/clinician${redirectQuery}`}
              className="group block w-full p-6 bg-white border-2 border-clinic-primary-lighter rounded-card shadow-clinic-sm hover:shadow-clinic hover:border-clinic-primary transition-all"
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 bg-clinic-gold/20 rounded-full flex items-center justify-center group-hover:bg-clinic-gold/30 transition-colors">
                    <ClipboardDocumentCheckIcon className="w-7 h-7 text-clinic-gold" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-lg font-semibold text-clinic-dark font-body">I'm a Clinician</h3>
                  <p className="text-sm text-clinic-muted mt-1">
                    Join as a mental health or allied professional
                  </p>
                </div>
                <div className="ml-4">
                  <ArrowRightIcon className="w-5 h-5 text-clinic-muted group-hover:text-clinic-gold group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>
          </div>

          {/* Divider */}
          <div className="my-8 flex items-center">
            <div className="flex-1 border-t border-clinic-primary-lighter" />
            <span className="px-4 text-sm text-clinic-muted">Already have an account?</span>
            <div className="flex-1 border-t border-clinic-primary-lighter" />
          </div>

          {/* Sign In Link */}
          <Link
            to={`/login${redirectQuery}`}
            className="block w-full text-center px-6 py-3 border-2 border-clinic-primary text-clinic-primary font-medium rounded-button hover:bg-clinic-primary hover:text-clinic-cream transition-colors"
          >
            Sign in to your account
          </Link>

          {/* Footer Links */}
          <div className="mt-8 pt-6 border-t border-clinic-primary-lighter text-center">
            <div className="flex justify-center space-x-6 text-sm text-clinic-muted">
              <Link to="/privacy" className="hover:text-clinic-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-clinic-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterLanding;
